import _ from "lodash";
import update from "immutability-helper";
import ACLStore from "lib/aclStore";
import client from "lib/apolloClient/client";

import {
  updateAttachmentMutation,
  deleteAttachmentMutation,
  updateLockedStateMutation,
} from "./Mutation";
import {
  getItemTypeLocale,
  isAssessment,
  isFolder,
} from "Utils/EntityFlowUtils";
import {
  getEntireUnitFlowFeedQuery,
  getUnitFlowItemCountInUnitPlanQuery,
} from "UnitFlowV3/modules/LearningCourseFlow/Query";
import {
  getUnitCombinedLibraryQueryFromCache,
  writeUnitCombinedLibraryQueryInCache,
} from "./GraphqlHelper";
import {
  getEntireUnitFlowFeedQueryFromCache,
  writeUnitFlowItemInCache,
  getUnitFlowItemFromCache,
} from "UnitFlowV3/modules/LearningCourseFlow/GraphqlHelper";
import {
  getToastMsgForResourceUpdate,
  getUnitFlowFeedVariablesMemoized,
} from "UnitFlowV3/modules/LearningCourseFlow/utils";
import { getUnitCombinedLibraryQuery } from "./Query";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";

import { getCombinedLibraryFeedVariablesMemoized } from "./utils";
import { deleteAssessmentMutation } from "Assessments/modules/AssessmentMutations";
import {
  removeUnitFlowResourcesFromFeed,
  updateAncestorChildCountRecursively,
  recalculateMaxDepth,
  rollbackResourcesInCache,
} from "UnitFlowV3/modules/LearningCourseFlow/Module";
import { getSelectedLearningCourseId } from "LearningCourse/modules/Utils";

import { getUpdatedTaggedPractices } from "SnP_v2/modules/SnPUtils";
import { LOCKING_SCOPE } from "CourseFlow/modules/Utils";
import { i18n } from "lib/i18n";
import { dispatchError } from "Utils";
import { getCurrentAssessmentTypes } from "Platform/modules/PlatformModule";
import { getUnitPlanFieldResponseFilter } from "Teacher/routes/Courses/routes/Course/routes/UnitPlans/modules/Utils";
import { getUnitPlanCentralRepoInputAndFilters } from "Teacher/routes/Courses/modules/utils";
import { getSelectedAcademicYearId } from "modules/CommonSelectors";
import {
  getUnitPlanDetailsFromCache,
  writeUnitPlanFragment,
} from "IBPlanner/modules/IBGraphqlHelpers";
import { getUnitPlanQuery } from "IBPlanner/modules/IBQuery";

export const updateCombinedLibraryResources = ({ id, label, attachment }) => {
  return async (dispatch, getState) => {
    const state = getState();

    // variables for updating right pane resources
    const unitPlanId = _.get(state, "planner.unitPlanData.unitPlanId", "");

    const curriculumType = _.get(
      state,
      "platform.currentCurriculumProgram.type",
      null
    );
    const learningCourseId = _.get(
      state,
      "planner.unitPlanData.learningCourseId",
      null
    );

    const combinedLibraryFilters = _.get(
      state,
      "unitPlans.combinedLibrary.filters",
      {}
    );

    const feedQueryVariables = getCombinedLibraryFeedVariablesMemoized({
      unitPlanId,
      filters: combinedLibraryFilters,
      learningCourseId,
    });

    const variables = {
      input: {
        id,
        name: label,
        ...attachment,
      },
    };

    // variables for updating unit flow feed middlepane
    const filters = _.get(state, "unitFlow.filters", {});

    const userInfo = getUserInfo({ portalType: "PLANNER" });

    const unitFlowfeedQueryVariables = getUnitFlowFeedVariablesMemoized({
      unitPlanId,
      filters,
      userInfo,
      curriculumType,
      learningCourseId,
    });

    const rollbackResources = [];
    try {
      const unitFlowFeedData = getEntireUnitFlowFeedQueryFromCache(
        unitFlowfeedQueryVariables
      );

      const unitFlowFeed = _.cloneDeep(
        _.get(
          unitFlowFeedData,
          "node.learningCourseFlowFeedWrapper.learningCourseFlowFeed.edges",
          []
        )
      );
      _.forEach(unitFlowFeed, edge => {
        const { node } = edge;
        if (node.item?.id !== id) return;

        const cachedUnitFlowNode = getUnitFlowItemFromCache({
          id: node.id,
          variables: unitFlowfeedQueryVariables,
        });

        if (cachedUnitFlowNode) {
          // used to rollback on error
          rollbackResources.push(cachedUnitFlowNode);

          const unitFlowNode = _.cloneDeep(cachedUnitFlowNode);

          unitFlowNode.label = label;

          if (unitFlowNode?.item?.attachmentType) {
            const nodeAttachment = unitFlowNode.item;
            nodeAttachment.name = label;
            if (nodeAttachment.attachmentType === "LINK") {
              nodeAttachment.url = attachment.url;
              nodeAttachment.thumbUrl = _.get(attachment, "thumbUrl", "");
            } else if (nodeAttachment.attachmentType === "NOTE") {
              nodeAttachment.name = label;
              nodeAttachment.attachmentTitle = label;
              nodeAttachment.title = label;
            } else {
              nodeAttachment.name = label;
            }
          }

          writeUnitFlowItemInCache({
            id: node.id,
            variables: unitFlowfeedQueryVariables,
            data: unitFlowNode,
          });
        }
      });

      await client.mutate({
        mutation: updateAttachmentMutation,
        variables,
        optimisticResponse: {
          __typename: "Mutation",
          platform: {
            updateAttachment: {
              id,
              __typename: "Attachment",
            },
            __typename: "PlatformMutations",
          },
        },
        update: cache => {
          cache.updateQuery(
            {
              query: getUnitCombinedLibraryQuery,
              variables: feedQueryVariables,
            },
            data => {
              if (!data) return;

              const unitResources = _.cloneDeep(
                _.get(
                  data,
                  "node.learningCourseFlowFeedWrapper.learningCourseFlowFeed.edges",
                  {}
                )
              );
              const itemIdx = _.findIndex(
                unitResources,
                ({ node }) => node.item.id === id
              );
              const newItem = _.cloneDeep(unitResources[itemIdx]);

              if (newItem) {
                newItem.node.label = label;

                const nodeAttachment = newItem.node.item;
                nodeAttachment.name = label;
                if (nodeAttachment.attachmentType === "LINK") {
                  nodeAttachment.url = attachment.url;
                  nodeAttachment.thumbUrl = _.get(attachment, "thumbUrl", "");
                } else if (nodeAttachment.attachmentType === "NOTE") {
                  nodeAttachment.name = label;
                  nodeAttachment.attachmentTitle = label;
                  nodeAttachment.title = label;
                } else {
                  nodeAttachment.name = label;
                }

                return update(data, {
                  node: {
                    learningCourseFlowFeedWrapper: {
                      learningCourseFlowFeed: {
                        edges: {
                          [itemIdx]: { $set: newItem },
                        },
                      },
                    },
                  },
                });
              }
            }
          );

          cache.updateQuery(
            {
              query: getEntireUnitFlowFeedQuery,
              variables: unitFlowfeedQueryVariables,
            },
            data => {
              if (!data) return;

              const updatedEdges = _.map(unitFlowFeed, edge => {
                const { node } = edge;
                if (node.item?.id === id) node.label = label;
                return edge;
              });

              return update(data, {
                node: {
                  learningCourseFlowFeedWrapper: {
                    learningCourseFlowFeed: {
                      edges: { $set: updatedEdges },
                    },
                  },
                },
              });
            }
          );
        },
      });
      dispatch(setToastMsg(getToastMsgForResourceUpdate("FILE")));
    } catch (error) {
      rollbackResourcesInCache({ rollbackResources });

      console.error(error);
      if (error.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
    }
  };
};

export const deleteCombinedLibraryResources = ({ ids }) => {
  return async (dispatch, getState) => {
    const state = getState();
    // variables for updating right pane resources
    const unitPlanId = _.get(state, "planner.unitPlanData.unitPlanId", "");

    const combinedLibraryFilters = _.get(
      state,
      "unitPlans.combinedLibrary.filters",
      {}
    );
    const curriculumType = _.get(
      state,
      "platform.currentCurriculumProgram.type",
      null
    );
    const learningCourseId = getSelectedLearningCourseId(state);

    const feedQueryVariables = getCombinedLibraryFeedVariablesMemoized({
      unitPlanId,
      filters: combinedLibraryFilters,
      learningCourseId,
    });

    // variables for updating unit flow feed
    const filters = _.get(state, "unitFlow.filters", {});

    const userInfo = getUserInfo({ portalType: "PLANNER" });

    const unitFlowfeedQueryVariables = getUnitFlowFeedVariablesMemoized({
      unitPlanId,
      filters,
      userInfo,
      curriculumType,
      learningCourseId,
    });

    let countOfDeletedItems = 0;

    try {
      await client.mutate({
        mutation: deleteAttachmentMutation,
        refetchQueries: [
          {
            query: getUnitFlowItemCountInUnitPlanQuery,
            variables: { id: unitPlanId },
          },
        ],
        variables: { input: ids },
        optimisticResponse: {
          __typename: "Mutation",
          platform: {
            deleteAttachments: {
              isSuccess: true,
              __typename: "DeleteAttachmentsResponse",
            },
            __typename: "PlatformMutations",
          },
        },
        update: cache => {
          cache.updateQuery(
            {
              query: getUnitCombinedLibraryQuery,
              variables: feedQueryVariables,
            },
            data => {
              if (!data) return;

              const unitResources = _.cloneDeep(
                _.get(
                  data,
                  "node.learningCourseFlowFeedWrapper.learningCourseFlowFeed.edges",
                  {}
                )
              );

              const updatedEdges = _.filter(
                unitResources,
                ({ node }) => !_.includes(ids, node.item.id)
              );

              return update(data, {
                node: {
                  learningCourseFlowFeedWrapper: {
                    learningCourseFlowFeed: {
                      edges: { $set: updatedEdges },
                    },
                  },
                },
              });
            }
          );

          cache.updateQuery(
            {
              query: getEntireUnitFlowFeedQuery,
              variables: unitFlowfeedQueryVariables,
            },
            data => {
              if (!data) return;

              const unitFlowFeed = _.cloneDeep(
                _.get(
                  data,
                  "node.learningCourseFlowFeedWrapper.learningCourseFlowFeed"
                )
              );

              const unitFlowFeedNodes = _.map(unitFlowFeed.edges, "node");
              const itemIdsToDelete = _.map(
                _.filter(unitFlowFeed?.edges, ({ node }) => {
                  const { item } = node;
                  return item && _.includes(ids, item?.id);
                }),
                ({ node }) => node.id
              );

              const items = _.filter(unitFlowFeedNodes, ({ id }) =>
                _.includes(itemIdsToDelete, id)
              );

              // update children and count of parents
              _.forEach(items, item => {
                const parent = _.find(
                  unitFlowFeedNodes,
                  ({ id }) => id === item.parent?.id
                );

                if (parent) {
                  parent.childrenV2 = _.filter(
                    parent.childrenV2,
                    ({ id }) => id !== item.id
                  );

                  updateAncestorChildCountRecursively({
                    id: parent.id,
                    items: unitFlowFeed.edges,
                    factor: isFolder(item) ? 0 : -1,
                  });

                  recalculateMaxDepth({
                    items: unitFlowFeed.edges,
                    id: parent.id,
                  });
                }
              });

              countOfDeletedItems = _.reduce(
                items,
                (result, item) => {
                  result += _.get(item, "childrenHierarchyInfo.totalCount", 0);
                  result += isFolder(item) ? 0 : 1;
                  return result;
                },
                0
              );

              const updatedFeed = {
                ...unitFlowFeed,
                edges: _.filter(
                  unitFlowFeed.edges,
                  ({ node }) => !_.includes(itemIdsToDelete, node.id)
                ),
                resourceTypeBasedCount: {
                  ...unitFlowFeed?.resourceTypeBasedCount,
                  totalCount:
                    _.get(
                      unitFlowFeed,
                      "resourceTypeBasedCount.totalCount",
                      0
                    ) - countOfDeletedItems,
                },
              };

              return update(data, {
                node: {
                  learningCourseFlowFeedWrapper: {
                    learningCourseFlowFeed: { $set: updatedFeed },
                  },
                },
              });
            }
          );
        },
      });
    } catch (error) {
      console.error(error);
      if (error.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
    }
  };
};

export const deleteCombinedLibraryAssessment = ({ assessmentId }) => {
  return async (dispatch, getState) => {
    const state = getState();
    const unitPlanId = _.get(state, "planner.unitPlanData.unitPlanId", "");
    const learningCourseId = getSelectedLearningCourseId(state);
    // Get combined library filters
    const combinedLibraryFilters = _.get(
      state,
      "unitPlans.combinedLibrary.filters",
      {}
    );

    const feedQueryVariables = getCombinedLibraryFeedVariablesMemoized({
      unitPlanId,
      filters: combinedLibraryFilters,
      learningCourseId,
    });

    try {
      await client.mutate({
        mutation: deleteAssessmentMutation,
        refetchQueries: [
          {
            query: getUnitFlowItemCountInUnitPlanQuery,
            variables: { id: unitPlanId },
          },
        ],
        variables: {
          id: assessmentId,
        },
        optimisticResponse: {
          __typename: "Mutation",
          planner: {
            __typename: "PlannerMutations",
            deleteAssessment: true,
          },
        },
        update: (
          cache,
          {
            data: {
              planner: { deleteAssessment },
            },
          }
        ) => {
          if (deleteAssessment) {
            // Update the combined library cache
            cache.updateQuery(
              {
                query: getUnitCombinedLibraryQuery,
                variables: feedQueryVariables,
              },
              data => {
                if (!data) return;

                const unitResources = _.cloneDeep(
                  _.get(
                    data,
                    "node.learningCourseFlowFeedWrapper.learningCourseFlowFeed.edges",
                    {}
                  )
                );

                const updatedEdges = _.filter(
                  unitResources,
                  ({ node }) => node.item.id !== assessmentId
                );

                return update(data, {
                  node: {
                    learningCourseFlowFeedWrapper: {
                      learningCourseFlowFeed: {
                        edges: { $set: updatedEdges },
                      },
                    },
                  },
                });
              }
            );

            // Remove the assessment from the unit flow feed
            dispatch(removeUnitFlowResourcesFromFeed({ ids: [assessmentId] }));
          }
        },
      });
    } catch (error) {
      console.error(error);
      if (error.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
    }
  };
};

export const updateTaggedPracticesInCombinedLibrary = ({
  combinedLibraryFeedVariables,
  itemId,
  addedPractices,
  removedPractices,
  evaluationCycleId,
  userId,
}) => {
  const unitCombinedLibraryFeed = getUnitCombinedLibraryQueryFromCache(
    combinedLibraryFeedVariables
  );

  const edges = _.cloneDeep(
    _.get(
      unitCombinedLibraryFeed,
      "node.learningCourseFlowFeedWrapper.learningCourseFlowFeed.edges",
      []
    )
  );

  const updatedEdges = _.map(edges, edge => {
    const node = _.get(edge, "node", {});

    if (
      _.get(node, "resourceType", "") !== "ASSESSMENT" ||
      _.get(node, "item.id", "") !== itemId
    )
      return edge;

    const taggedPractices = node?.item.taggedPractices ?? [];

    const updatedTaggedPractices = getUpdatedTaggedPractices({
      taggedPractices,
      evaluationCycleId,
      addedPractices,
      removedPractices,
      userId,
    });

    const updatedNode = {
      ...node,
      item: {
        ...node.item,
        taggedPractices: updatedTaggedPractices,
      },
    };

    const updatedEdge = { ...edge, node: updatedNode };

    return updatedEdge;
  });

  const updatedData = update(unitCombinedLibraryFeed, {
    node: {
      learningCourseFlowFeedWrapper: {
        learningCourseFlowFeed: {
          edges: { $set: updatedEdges },
        },
      },
    },
  });

  writeUnitCombinedLibraryQueryInCache({
    variables: combinedLibraryFeedVariables,
    data: updatedData,
  });
};

export const updateLockedState = ({ type, id }) => {
  return async (dispatch, getState) => {
    const state = getState();
    const userInfo = getUserInfo({ portalType: "PLANNER" });
    const academicYearId = getSelectedAcademicYearId(state);

    const lockScope = LOCKING_SCOPE.SELF;
    let entityType = "LEARNING_COURSE_FLOW",
      entityId = id;

    const unitPlanId = _.get(state, "planner.unitPlanData.unitPlanId", "");
    const learningCourseId = _.get(
      state,
      "planner.unitPlanData.learningCourseId",
      getSelectedLearningCourseId(state)
    );

    // Get combined library filters
    const combinedLibraryFilters = _.get(
      state,
      "unitPlans.combinedLibrary.filters",
      {}
    );

    const feedQueryVariables = getCombinedLibraryFeedVariablesMemoized({
      unitPlanId,
      filters: combinedLibraryFilters,
      learningCourseId,
    });

    const unitCombinedLibraryFeed = getUnitCombinedLibraryQueryFromCache(
      feedQueryVariables
    );

    const isLocked = !_.includes(type, "UNLOCK");

    const edges = _.get(
      unitCombinedLibraryFeed,
      "node.learningCourseFlowFeedWrapper.learningCourseFlowFeed.edges",
      []
    );

    const idx = _.findIndex(edges, ({ node }) => node.id === id);

    if (idx === -1) return;

    const item = _.get(edges, `[${idx}].node`, {});

    const assessmentTypes = dispatch(getCurrentAssessmentTypes());
    const entityLabelLocale = getItemTypeLocale({
      item,
      assessmentTypes,
    });

    const isItemAssessment = isAssessment(item);

    if (isItemAssessment) {
      entityType = "ASSESSMENT";
      entityId = item?.item?.id;
    }

    const lockingInfo = { isLocked };

    const updatedUnitCombinedLibraryFeed = update(unitCombinedLibraryFeed, {
      node: {
        learningCourseFlowFeedWrapper: {
          learningCourseFlowFeed: {
            edges: {
              [idx]: {
                node: {
                  ...(isItemAssessment
                    ? {
                        item: {
                          lockingInfo: { $set: lockingInfo },
                        },
                      }
                    : { lockingInfo: { $set: lockingInfo } }),
                },
              },
            },
          },
        },
      },
    });

    try {
      writeUnitCombinedLibraryQueryInCache({
        variables: feedQueryVariables,
        data: updatedUnitCombinedLibraryFeed,
      });

      const variables = {
        input: {
          learningCourseId,
          lockType: "CONTENT_LEVEL_LOCK",
          lockScope,
          entityLocks: [
            {
              entityId,
              entityType,
              isLocked,
            },
          ],
        },
      };

      const { centralRepoInput } = getUnitPlanCentralRepoInputAndFilters({
        organizationId: userInfo.org_id,
        courseId: _.get(state, "teacher.selected_class.selected_course", ""),
        academicYearId,
      });

      const responseFilter = getUnitPlanFieldResponseFilter();

      const collaboratorsFilter = {
        userAcademicYearId: academicYearId,
      };

      const refetchQueries = [];

      if (!isLocked) {
        // If unlocking, we need to refetch the unit plan details to update the unit-flow lock state
        const unitPlanQueryVariables = {
          id: unitPlanId,
          centralRepoInput,
          responseFilter,
          collaboratorsFilter,
          exceptionalCollaboratorsFilter: collaboratorsFilter,
          skipTitle: ACLStore.can(
            "FeatureFlag:RemoveCourseDetailsFromStaffList"
          ),
        };

        refetchQueries.push({
          query: getUnitPlanQuery,
          variables: unitPlanQueryVariables,
        });
      } else {
        // If locking, we need to update the unit plan details in cache
        const unitPlanDetails = getUnitPlanDetailsFromCache({
          unitPlanId,
          centralRepoInput,
          responseFilter,
          collaboratorsFilter,
          exceptionalCollaboratorsFilter: collaboratorsFilter,
        });

        // Update the unit flow lock state in unit plan details
        const updatedUnitPlanDetails = update(unitPlanDetails, {
          isUnitFlowLocked: { $set: true },
        });

        // Write the updated unit plan details in cache
        writeUnitPlanFragment({
          unitPlanId,
          data: updatedUnitPlanDetails,
          responseFilter,
          centralRepoInput,
          collaboratorsFilter,
          exceptionalCollaboratorsFilter: collaboratorsFilter,
        });
      }

      await client.mutate({
        mutation: updateLockedStateMutation,
        variables,
        optimisticResponse: {
          __typename: "Mutation",
          planner: {
            bulkUpdateLearningCourseEntityLock: {
              isSuccess: true,
            },
            __typename: "PlannerMutations",
          },
        },
        refetchQueries,
      });

      const t = i18n.t;
      dispatch(
        setToastMsg({
          msg: t("course:locking_action_item_toast", {
            lockingAction: isLocked ? t("common:locked") : t("common:unlocked"),
            entityType: t(entityLabelLocale),
          }),
          notString: true,
          type: "success",
          position: "toast-bottom-left",
        })
      );
    } catch (error) {
      // Rollback the cache update in case of error
      writeUnitCombinedLibraryQueryInCache({
        variables: feedQueryVariables,
        data: unitCombinedLibraryFeed,
      });

      console.error(error);
      dispatchError({ dispatch, error });
    }
  };
};
